import {
  convertFromIFeatureResponseToIFeatureGlobalData,
  ICreateFeatureIdentityRequest,
  ICreateFeatureRoleRequest,
  IDeleteFeatureIdentityRequest,
  IDeleteFeatureRoleRequest,
  IFeatureGlobalData,
  IFeatureResponse,
  IUpdateFeatureIdentityRequest,
  IUpdateFeatureRequest,
  IUpdateFeatureRoleRequest,
} from '../../types/administration/features/IFeature';
import { BaseAPI } from '../BaseApi';
import { eAPITags } from '../eAPITags';
import { nameComparator } from '../../utilities/labelComparator';

export const FeaturesApi = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    listFeatures: build.query<IFeatureGlobalData[], void>({
      query: () => '/timber/api/internal/features',
      transformResponse: (responseList: IFeatureResponse[]) =>
        convertFromIFeatureResponseToIFeatureGlobalData(responseList).toSorted(nameComparator),
      providesTags: () => [{ type: eAPITags.FeatureFlags }],
    }),
    updateFeature: build.mutation<IFeatureResponse, IUpdateFeatureRequest>({
      query: ({ name, isOn }) => ({
        url: `/timber/api/internal/features/${name}`,
        method: 'PATCH',
        body: { isOn },
      }),
      invalidatesTags: () => [{ type: eAPITags.FeatureFlags }, { type: eAPITags.Config }],
    }),
    createFeatureRole: build.mutation<IFeatureResponse, ICreateFeatureRoleRequest>({
      query: ({ name, roleId, isOn }) => ({
        url: `/timber/api/internal/features/${name}/roles/${roleId}`,
        method: 'POST',
        body: { isOn },
      }),
      invalidatesTags: () => [{ type: eAPITags.FeatureFlags }, { type: eAPITags.Config }],
    }),
    updateFeatureRole: build.mutation<IFeatureResponse, IUpdateFeatureRoleRequest>({
      query: ({ name, roleId, isOn }) => ({
        url: `/timber/api/internal/features/${name}/roles/${roleId}`,
        method: 'PATCH',
        body: { isOn },
      }),
      invalidatesTags: () => [{ type: eAPITags.FeatureFlags }, { type: eAPITags.Config }],
    }),
    deleteFeatureRole: build.mutation<void, IDeleteFeatureRoleRequest>({
      query: ({ name, roleId }) => ({
        url: `/timber/api/internal/features/${name}/roles/${roleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: eAPITags.FeatureFlags }, { type: eAPITags.Config }],
    }),
    createFeatureIdentity: build.mutation<IFeatureResponse, ICreateFeatureIdentityRequest>({
      query: ({ name, identityId, isOn }) => ({
        url: `/timber/api/internal/features/${name}/identities/${identityId}`,
        method: 'POST',
        body: { isOn },
      }),
      invalidatesTags: () => [{ type: eAPITags.FeatureFlags }, { type: eAPITags.Config }],
    }),
    updateFeatureIdentity: build.mutation<IFeatureResponse, IUpdateFeatureIdentityRequest>({
      query: ({ name, identityId, isOn }) => ({
        url: `/timber/api/internal/features/${name}/identities/${identityId}`,
        method: 'PATCH',
        body: { isOn },
      }),
      invalidatesTags: () => [{ type: eAPITags.FeatureFlags }, { type: eAPITags.Config }],
    }),
    deleteFeatureIdentity: build.mutation<void, IDeleteFeatureIdentityRequest>({
      query: ({ name, identityId }) => ({
        url: `/timber/api/internal/features/${name}/identities/${identityId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: eAPITags.FeatureFlags }, { type: eAPITags.Config }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListFeaturesQuery,
  useUpdateFeatureMutation,
  useCreateFeatureRoleMutation,
  useUpdateFeatureRoleMutation,
  useDeleteFeatureRoleMutation,
  useCreateFeatureIdentityMutation,
  useUpdateFeatureIdentityMutation,
  useDeleteFeatureIdentityMutation,
} = FeaturesApi;
